import { BANK_SUBTYPES, CARD_SCHEMES } from "@/lib/constants";
import { bankOrFundManagerNameSchema } from "@/lib/bankOrFundManagerNameSchema";
import * as z from "zod";

export const accountFormSchema = z
  .object({
    name: z.string().min(1, "Account name is required"),
    nickname: z
      .string()
      .max(50, "Nickname must be 50 characters or less")
      .regex(/^[a-zA-Z0-9 ]*$/, "Nickname may only contain letters, numbers, and spaces")
      .optional()
      .or(z.literal(""))
      .transform((v) => (v === "" ? undefined : v)),
    account_number: z.string().optional(),
    type: z.enum(["bank", "mobile_money", "card"] as const),
    currency: z.string().min(3, "Currency is required"),
    balance: z.number(),
    status: z.enum(["active", "inactive"] as const).optional(),
    subtype: z.enum(BANK_SUBTYPES).optional(),
    bank_name: bankOrFundManagerNameSchema.optional().or(z.literal("")).transform((v) => (v === "" ? undefined : v)),
    mobile_money_provider: z
      .string()
      .max(100, "Provider name must be 100 characters or less")
      .optional(),
    mobile_money_provider_other: z
      .string()
      .max(100, "Provider name must be 100 characters or less")
      .optional(),
    card_provider: z
      .string()
      .max(100, "Card provider must be 100 characters or less")
      .optional(),
    card_scheme: z.enum(CARD_SCHEMES).optional(),
    interest_rate: z
      .number()
      .min(0, "Interest rate must be non-negative")
      .max(100, "Interest rate must be 100 or less")
      .nullable()
      .optional(),
    sort_code: z.string().optional().or(z.literal("")).transform((v) => (v === "" ? undefined : v)),
    iban: z.string().optional().or(z.literal("")).transform((v) => (v === "" ? undefined : v)),
    swift_code: z.string().optional().or(z.literal("")).transform((v) => (v === "" ? undefined : v)),
    routing_number: z.string().optional().or(z.literal("")).transform((v) => (v === "" ? undefined : v)),
    transit_number: z.string().optional().or(z.literal("")).transform((v) => (v === "" ? undefined : v)),
    institution_number: z.string().optional().or(z.literal("")).transform((v) => (v === "" ? undefined : v)),
    bank_address: z
      .string()
      .max(500, "Bank address must be 500 characters or less")
      .optional()
      .or(z.literal(""))
      .transform((v) => (v === "" ? undefined : v)),
  })
  .refine(
    (data) => {
      // Bank name required when type is 'bank'
      if (data.type === "bank" && !data.bank_name) {
        return false;
      }
      return true;
    },
    {
      message: "Bank name is required for bank accounts",
      path: ["bank_name"],
    },
  )
  .refine(
    (data) => {
      // Mobile money provider required when type is 'mobile_money'
      if (data.type === "mobile_money" && !data.mobile_money_provider) {
        return false;
      }
      return true;
    },
    {
      message: "Mobile money provider is required",
      path: ["mobile_money_provider"],
    },
  )
  .refine(
    (data) => {
      // Custom provider required when 'Other' is selected
      if (
        data.type === "mobile_money" &&
        data.mobile_money_provider === "Other" &&
        !data.mobile_money_provider_other
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Please specify the provider name",
      path: ["mobile_money_provider_other"],
    },
  )
  .refine(
    (data) => {
      // Card provider required when type is 'card'
      if (data.type === "card" && !data.card_provider) {
        return false;
      }
      return true;
    },
    {
      message: "Card provider is required for card accounts",
      path: ["card_provider"],
    },
  )
  .refine(
    (data) => {
      // Card scheme required when type is 'card'
      if (data.type === "card" && !data.card_scheme) {
        return false;
      }
      return true;
    },
    {
      message: "Card scheme is required for card accounts",
      path: ["card_scheme"],
    },
  );
